import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ArrowDownLeft, ArrowUpRight } from "lucide-react";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

function DueCard({ title, data, icon: Icon, tone, active, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`flex flex-1 items-start gap-3 rounded-2xl border p-4 text-left transition-colors ${active ? "border-[#0D5C3A] bg-[#F0ECE1]" : "border-[#E5E0D8] bg-white hover:border-[#84A98C]"}`}>
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tone}`}><Icon className="h-5 w-5" /></div>
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-wider text-stone-400">{title}</p>
        <p className="font-heading text-2xl font-extrabold text-stone-900" data-testid={`${testid}-amount`}>{inr(data.pending.amount)}</p>
        <p className="text-xs text-stone-500">{data.pending.count} pending · {inr(data.paid.amount)} paid ({data.paid.count})</p>
      </div>
    </button>
  );
}

export function DuesSummary({ payment, onPayment, refreshKey }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/orders/dues").then((r) => setD(r.data)).catch(() => setD({})); }, [refreshKey]);
  if (!d || (!d.receivable && !d.payable)) return null;
  const chips = [["all", "All payments"], ["pending", "Payment pending"], ["paid", "Paid"]];
  return (
    <div className="space-y-3" data-testid="dues-summary">
      <div className="flex flex-col gap-3 sm:flex-row">
        {d.receivable && <DueCard title="Owed to you" data={d.receivable} icon={ArrowDownLeft} tone="bg-emerald-50 text-emerald-700" active={payment === "pending"} onClick={() => onPayment("pending")} testid="dues-receivable" />}
        {d.payable && <DueCard title="You owe" data={d.payable} icon={ArrowUpRight} tone="bg-[#FDF6F2] text-[#C85A32]" active={payment === "pending"} onClick={() => onPayment("pending")} testid="dues-payable" />}
      </div>
      <div className="flex flex-wrap gap-2">
        {chips.map(([k, l]) => (
          <button key={k} type="button" onClick={() => onPayment(k)} data-testid={`payment-filter-${k}`}
            className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${payment === k ? "border-[#C85A32] bg-[#C85A32] text-white" : "border-[#E5E0D8] bg-white text-stone-600 hover:border-[#C85A32]"}`}>{l}</button>
        ))}
      </div>
      {d.by_partner?.length > 0 && (
        <details className="rounded-2xl border border-[#E5E0D8] bg-white p-4" data-testid="dues-by-partner">
          <summary className="cursor-pointer text-sm font-semibold text-stone-800">Pending dues by partner ({d.by_partner.length})</summary>
          <div className="mt-3 divide-y divide-[#F0ECE1]">
            {d.by_partner.map((p) => (
              <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm" data-testid={`dues-partner-${p.code}`}>
                <div><p className="font-semibold text-stone-800">{p.name}</p><p className="font-mono text-xs text-stone-400">{p.code} · {p.count} order{p.count > 1 ? "s" : ""} · oldest {new Date(p.oldest).toLocaleDateString("en-IN")}</p></div>
                <div className="text-right">
                  {p.owed_to_you > 0 && <p className="font-semibold text-emerald-700">Owes you {inr(p.owed_to_you)}</p>}
                  {p.you_owe > 0 && <p className="font-semibold text-[#C85A32]">You owe {inr(p.you_owe)}</p>}
                </div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

export function PaymentBadge({ status }) {
  if (!status) return null;
  const paid = status === "paid";
  return (
    <span data-testid="payment-badge" className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${paid ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
      {paid ? "Paid" : "Payment pending"}
    </span>
  );
}
